// src/pages/HRBPTracker/TaskDetails/ViewAuditLogModal.tsx

import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../../Redux/Actions';
import { FiPlusCircle, FiEdit2, FiTrash2 } from 'react-icons/fi';
import './ViewAuditLogModal.scss';

interface AuditLog {
  _id: string;
  action: 'Created' | 'Updated' | 'Deleted';
  changedByName: string;
  timestamp: string;
  changes: {
    field: string;
    oldValue: any;
    newValue: any;
  }[];
}

const getActionDetails = (action: string) => {
  switch (action.toLowerCase()) {
    case 'created': return { Icon: FiPlusCircle, color: '#198754' };
    case 'updated': return { Icon: FiEdit2, color: '#0d6efd' };
    case 'deleted': return { Icon: FiTrash2, color: '#dc3545' };
    default: return { Icon: FiEdit2, color: '#6c757d' };
  }
};

const ViewAuditLogModal = ({ taskId }: { taskId: string | null }) => {
  const dispatch = useDispatch();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);

  const { 
    getHrbpActivityAuditLog,
    getEmployeeData,
    getClientNameDetails,
  } = useSelector((state: any) => state.employeeReducer);

  const employeeMap = useMemo(() => {
    const map = new Map<string, string>();
    (getEmployeeData?.data || []).forEach((emp: any) => {
      map.set(emp._id, `${emp.candidateName} ${emp.lastname || ''}`);
    });
    return map;
  }, [getEmployeeData]);

  const clientMap = useMemo(() => {
    const map = new Map<string, string>();
    (getClientNameDetails?.data || []).forEach((client: any) => {
      map.set(client._id, client.clientName);
    });
    return map;
  }, [getClientNameDetails]);
  
  const formatChangeValue = (value: any, field: string) => {
    if (value === null || value === undefined || value === "") return <span className="empty-value">empty</span>;
    switch (field) {
      case 'employeeId':
      case 'assignedHrbpId':
        return employeeMap.get(String(value)) || String(value);
      case 'clientId':
        return clientMap.get(String(value)) || String(value);
      default:
        const date = new Date(value);
        if (!isNaN(date.getTime()) && String(value).includes('T')) {
          return date.toLocaleDateString();
        }
        return String(value);
    }
  };
  
  useEffect(() => {
    if (taskId) {
      setLogs([]);
      setLoading(true);
      
      // 3. Dispatch the action to fetch new data.
      dispatch(actions.getHrbpActivityAuditLogAction(taskId));
      
      // Ensure all lookup data is available (this part is unchanged)
      if (!getEmployeeData?.data) dispatch(actions.getEmployeeDataAction());
      if (!getClientNameDetails?.data) dispatch(actions.getClientNameAction());
    }
  }, [taskId, dispatch]); 

  useEffect(() => {
    if (getHrbpActivityAuditLog) { 
      if (getHrbpActivityAuditLog.data) {
        setLogs(getHrbpActivityAuditLog.data);
      }
      setLoading(false);
    }
  }, [getHrbpActivityAuditLog]); 
  if (loading) {
    return <div className="audit-log-placeholder">Loading history...</div>;
  }
  if (!logs || logs.length === 0) {
    return <div className="audit-log-placeholder">No history found for this task.</div>;
  }

  return (
    <div className="audit-timeline-container">
      {logs.map((log) => {
        const { Icon, color } = getActionDetails(log.action);
        return (
          <div key={log._id} className="timeline-item">
            <div className="timeline-icon" style={{ backgroundColor: color }}>
              <Icon color="white" size={14} />
            </div>
            <div className="timeline-content">
              <div className="log-header">
                <strong className="log-action">{log.action}</strong>
                <span className="log-meta">
                  by {log.changedByName || 'System'} on {new Date(log.timestamp).toLocaleString()}
                </span>
              </div>
              <div className="log-changes">
                {log.changes.map((change, index) => (
                  <div key={index} className="change-detail">
                    <span className="change-field">{change.field}:</span>
                    <span className="change-value">
                      {formatChangeValue(change.oldValue, change.field)} → {formatChangeValue(change.newValue, change.field)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ViewAuditLogModal;